import React, {useCallback} from 'react';
import {Linking, Platform, Text, ScrollView} from 'react-native';
import {BrandName, isFullSwing} from '../../../constants';
import {useNavigation} from '@react-navigation/native';
import SupportGuideHeader from "../../../components/SupportGuideHeader";
import {colors} from "../../../assets/styles/colors";
import MainStyle from "../../../../MainStyle";
import Div from "../../../lib/Div";
import {NativeStackNavigationProp} from "@react-navigation/native-stack";
import FSHeader from "../../../components/FSHeader";

type Props = {
  onExit?: () => void;
};

const ResponsibleGaming = ({onExit: onExitOverride}: Props = {}) => {
  const navigation: NativeStackNavigationProp<any> = useNavigation();

  const onExit = useCallback(() => {
    if (onExitOverride) {
      onExitOverride();
      return;
    }

    if (navigation.canGoBack()) navigation.goBack();
    else navigation.navigate('challenges');
  }, [navigation, onExitOverride]);

  const openLink = (url: string) => {
    if (Platform.OS === 'web') window.open(url, '_blank');
    else Linking.openURL(url).then();
  };

  return (
    <>
      {isFullSwing ? (
        <FSHeader
          title="Responsible Gaming" goBack={onExit} hideLogo hideGameLogo hideAccount hideBalance hideMenu px={20}
        />
      ) : (
        <SupportGuideHeader title="Responsible Gaming" onExit={onExit}/>
      )}

      <ScrollView keyboardShouldPersistTaps="handled"
                  contentContainerStyle={[MainStyle.min_h_full, {backgroundColor: isFullSwing ? 'transparent' : colors.bg_contest, paddingBottom: 27}]}>
        <Div maxW={1120} w="100%" alignSelf="center" pt={17} px={40}>
          <Text style={styles.title_s}>General</Text>

          <Text style={styles.text_s}>
            {BrandName} strive to offer the most exciting complement to the sports viewing experience for you and
            your friends. Importantly, we want to provide you with that experience in a safe, reliable and responsible
            manner.
          </Text>

          <Text style={styles.text_s}>
            Please know that we are here to help you at any time.
          </Text>

          <Text style={styles.title_s}>Additional Information</Text>

          <Text style={styles.text_s}>
            Like many other addictions, compulsive play includes uncontrollable urges, which, if left unchecked, could
            result in financial hardship. Anyone could be susceptible to compulsive play regardless of age, gender,
            race, or financial status. Once detected, compulsive play can be successfully treated. The key is early
            detection in order to avoid adverse and dire consequences. If you feel you have a compulsive-play problem,
            we urge you to seek assistance.
          </Text>

          <Text style={styles.text_s}>Compulsive-play warning signs include:</Text>

          <Div row>
            <Text style={styles.dot_s}>&#8226;</Text>
            <Text style={[...styles.text_s, MainStyle.m_b_0]}>
              Playing more in order to recoup or chase previous losses.
            </Text>
          </Div>

          <Div row>
            <Text style={styles.dot_s}>&#8226;</Text>
            <Text style={[...styles.text_s, MainStyle.m_b_0]}>
              Sacrificing time from family or work in order to participate in tournaments.
            </Text>
          </Div>

          <Div row>
            <Text style={styles.dot_s}>&#8226;</Text>
            <Text style={[...styles.text_s, MainStyle.m_b_0]}>
              Repeated inability to stop or control participating.
            </Text>
          </Div>

          <Div row>
            <Text style={styles.dot_s}>&#8226;</Text>
            <Text style={[...styles.text_s, MainStyle.m_b_0]}>
              Borrowing money to participate or to pay off previous losses.
            </Text>
          </Div>

          <Div row>
            <Text style={styles.dot_s}>&#8226;</Text>
            <Text style={[...styles.text_s, MainStyle.m_b_0]}>
              Neglecting one's family or oneself due to excessive play.
            </Text>
          </Div>

          <Div row>
            <Text style={styles.dot_s}>&#8226;</Text>
            <Text style={[...styles.text_s, MainStyle.m_b_0]}>
              Lying or not being truthful about time or money lost to playing.
            </Text>
          </Div>

          <Div row>
            <Text style={styles.dot_s}>&#8226;</Text>
            <Text style={[...styles.text_s, MainStyle.m_b_0]}>
              Selling or pawning personal property to play.
            </Text>
          </Div>

          <Div row mb={15}>
            <Text style={styles.dot_s}>&#8226;</Text>
            <Text style={[...styles.text_s, MainStyle.m_b_0]}>Feelings of hopelessness, depression, or suicide.</Text>
          </Div>

          <Text style={styles.text_s}>
            If you or someone you know is or may be experiencing a compulsive-play problem, it is important to remember
            that assistance, intervention, and treatment are available.
          </Text>

          <Text style={styles.text_s}>
            Resources that are committed to providing professional and compassionate service to all interested persons
            include:
          </Text>

          <Text style={styles.text_s}>
            Call: 1.800-426-2537 or access {''}
            <Text style={styles.link_s} onPress={() => openLink('http://www.800gambler.org/')}>
              Gambling Problem Hotline in NJ | Gambling Addiction Treatment | 800-Gambler
            </Text>
          </Text>

          <Text style={styles.text_s}>
            Call: 1.800-522-4700 National Council on Problem Gambling or access {''}
            <Text style={styles.link_s} onPress={() => openLink('https://www.ncpgambling.org')}>
              Home - National Council on Problem Gambling
            </Text>
          </Text>

          <Text style={styles.text_s}>
            If you are in Arizona, call the toll-free Arizona statewide helpline at 1-800-NEXTSTEP or text NEXT-STEP to
            53342. You may also visit {''}
            <Text style={styles.link_s}
                  onPress={() => openLink('https://problemgambling.az.gov')}>
              Problem Gambling | Arizona Department of Gaming
            </Text>
          </Text>

          <Text style={styles.secondary_s}>
            If you are concerned that a family member is exhibiting warning signs of compulsive play behavior, please
            contact Customer Support for assistance. Eligible third parties may request that a consumer be excluded
            from{BrandName}. Proof of eligibility status will be requested in documentary form evidencing sole or joint
            financial responsibility for the source of any funds deposited with {BrandName} for gameplay including: (i)
            proof that the requestor is jointly obligated on the credit or debit card associated with the Consumer’s
            account; (ii) proof of legal dependency of the consumer on the requestor under state or federal law; (iii)
            proof of the existence of a court order that makes the requestor wholly or partially obligated for the debts
            of the person for whom exclusion is requested; or (iv) proof of the existence of a court order requiring the
            consumer to pay unmet child support obligations. {BrandName}, in its sole discretion, may request additional
            documentary verification information to establish third-party eligibility.
          </Text>
        </Div>
      </ScrollView>
    </>
  );
};

const styles = {
  text_s: [MainStyle.c_white, MainStyle.Barlow400, MainStyle.f_s_12, MainStyle.m_b_15, MainStyle.l_h_16],
  link_s: [MainStyle.c_lightblue, MainStyle.Barlow700, MainStyle.f_s_12, MainStyle.t_d_l_u, MainStyle.l_h_16],
  secondary_s: [MainStyle.c_white, MainStyle.Barlow400, MainStyle.f_s_10, MainStyle.m_b_15, MainStyle.l_h_16],
  title_s: [MainStyle.c_lightblue, MainStyle.Barlow700, MainStyle.f_s_16, MainStyle.m_b_15, MainStyle.t_t_u],
  dot_s: [MainStyle.c_white, MainStyle.Barlow400, MainStyle.f_s_12, MainStyle.l_h_16, MainStyle.m_x_8]
};

export default ResponsibleGaming;
